import { Component } from '@theme/component';

/** @extends {Component<{video: HTMLVideoElement, control: HTMLButtonElement}>} */
class KindneoFeatureFilm extends Component {
  requiredRefs = ['video', 'control'];
  #observer;
  #controller;
  #visible = false;
  #userPaused = false;
  #userInitiated = false;
  #loadFailed = false;
  #playing = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #playGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    this.#pause();
    super.updatedCallback();
    this.#bind();
  }

  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#visible = false;
    this.#loadFailed = false;
    this.refs.video.addEventListener('error', () => { this.#loadFailed = true; }, { capture: true, signal });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry?.isIntersecting ?? false;
      this.#updatePlayback();
    }, { threshold: 0.15 });
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', () => this.#updatePlayback(), { signal });
    this.#motion.addEventListener('change', () => {
      this.#userInitiated = false;
      this.#updatePlayback();
    }, { signal });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.#pause();
    super.disconnectedCallback();
  }

  /** Toggle the decorative film after an explicit visitor action. */
  toggle() {
    if (!this.#playing) {
      this.#userPaused = false;
      this.#userInitiated = true;
      if (this.refs.video.readyState < 2) this.#loadFailed = true;
      this.#play();
    } else {
      this.#userPaused = true;
      this.#pause();
    }
  }

  #updatePlayback() {
    // Respect a visitor's data and motion preferences unless they press Play.
    const connection = navigator.connection;
    const restricted = this.#motion.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
    if (this.#visible && !document.hidden && !this.#userPaused && (!restricted || this.#userInitiated)) {
      this.#play();
    } else {
      this.#pause();
    }
  }

  async #play() {
    if (!this.isConnected) return;
    const video = this.refs.video;
    // No video bytes are requested until the film is visible or Play is pressed.
    const needsSources = !video.querySelector('source[src]');
    if (needsSources) {
      for (const source of video.querySelectorAll('source[data-src]')) {
        source.src = source.dataset.src;
      }
    }
    if (needsSources || this.#loadFailed) {
      this.#loadFailed = false;
      video.load();
    }
    const generation = ++this.#playGeneration;
    try {
      await video.play();
      if (generation !== this.#playGeneration || !this.isConnected) return;
      this.#loadFailed = false;
      this.#playing = true;
      this.setAttribute('data-playing', '');
      this.refs.control.textContent = 'Ⅱ';
      this.refs.control.setAttribute('aria-label', this.refs.control.dataset.pauseLabel || '');
    } catch {
      if (generation === this.#playGeneration) this.#pause();
    }
  }

  #pause() {
    this.#playing = false;
    this.#playGeneration++;
    this.refs.video?.pause();
    if (this.refs.control) {
      this.refs.control.textContent = '▶';
      this.refs.control.setAttribute('aria-label', this.refs.control.dataset.playLabel || '');
    }
  }
}

if (!customElements.get('kn-feature-film')) customElements.define('kn-feature-film', KindneoFeatureFilm);
